import type { TemplateRegistration, TemplateData } from './types';
import { sanitizeText, sanitizeSeoText, escapeAttr } from './sanitize';
import { getIconSvg, renderStars, buildFontsUrl, buildNavLinks, NavDataFlags, truncateAtSentence } from './helpers';

export const royalCrest: TemplateRegistration = {
  meta: {
    id: 'royal-crest',
    name: 'Royal Crest',
    category: 'Classic',
    description: 'Regal blue and gold with balanced dual-column layout',
    previewImage: '',
    supportedFeatures: ['contact-form', 'phone-display', 'address-map', 'business-hours', 'about-section', 'services-list', 'testimonials', 'google-reviews', 'social-links', 'faq', 'price-list', 'promotions', 'photo-gallery'],
    defaultEnabledFeatures: ['contact-form', 'phone-display', 'about-section', 'services-list', 'testimonials'],
  },
  render(data: TemplateData, enabledFeatures: Set<string>): string {
    const safeName = sanitizeText(data.businessName);
    const safeHeadline = sanitizeText(data.headline);
    const safeAbout = sanitizeText(data.aboutText);
    const safeSeoTitle = sanitizeSeoText(data.seo.title, 70);
    const safeSeoDesc = sanitizeSeoText(data.seo.description, 160);

    const phone = data.businessData?.phone || null;
    const contactEmail = data.businessData?.contactEmail || null;
    const rating = data.businessData?.rating || null;
    const reviewCount = data.businessData?.reviewCount || 0;
    const businessHours = data.businessData?.businessHours || [];
    const photos = data.businessData?.photos || [];
    const address = data.businessData?.address || '';
    const googleMapsUrl = data.businessData?.googleMapsUrl || null;
    const heroImage = photos[0] || null;
    const galleryImages = photos.slice(1, 6);

    const navDataFlags: NavDataFlags = {
      hasServices: data.services.length > 0,
      hasTestimonials: data.testimonials.length > 0,
      hasGalleryPhotos: galleryImages.length > 0,
      hasFaq: !!(data.faq && data.faq.length > 0),
      hasPriceList: !!(data.priceList && data.priceList.length > 0),
    };
    const navLinks = buildNavLinks(enabledFeatures, navDataFlags);
    const fontsUrl = buildFontsUrl(['EB Garamond', 'Open Sans']);

    const sections: string[] = [];

    // --- Google Reviews ---
    if (enabledFeatures.has('google-reviews') && rating) {
      sections.push(`
      <section class="google-reviews">
        <div class="container">
          <div class="review-badge">
            <div class="review-stars">${renderStars(Math.round(rating))}</div>
            <span class="review-text">${rating.toFixed(1)} out of 5 &mdash; ${reviewCount} Google reviews</span>
          </div>
        </div>
      </section>`);
    }

    // --- Promotions ---
    if (enabledFeatures.has('promotions') && data.promotion) {
      const promoTitle = sanitizeText(data.promotion.title);
      const promoDesc = sanitizeText(data.promotion.description);
      const promoDiscount = sanitizeText(data.promotion.discount);
      sections.push(`
      <section class="promo-banner">
        <div class="container">
          <div class="promo-inner">
            <span class="promo-badge">${promoDiscount}</span>
            <div class="promo-content">
              <strong>${promoTitle}</strong>
              <span>${promoDesc}</span>
            </div>
          </div>
        </div>
      </section>`);
    }

    // --- About ---
    if (enabledFeatures.has('about-section')) {
      const firstChar = safeAbout.charAt(0);
      const restText = safeAbout.substring(1);
      sections.push(`
      <section id="about" class="section section-dark">
        <div class="container">
          <div class="gold-rule"></div>
          <h2 class="section-title">About Us</h2>
          <div class="about-story">
            <span class="drop-cap">${firstChar}</span>${restText}
          </div>
        </div>
      </section>`);
    }

    // --- Services ---
    if (enabledFeatures.has('services-list') && data.services.length > 0) {
      const serviceRows = data.services.map((s, i) => {
        const sName = sanitizeText(s.name);
        const sDesc = sanitizeText(s.description);
        const num = String(i + 1).padStart(2, '0');
        return `
          <div class="service-row">
            <span class="service-num">${num}</span>
            <div class="service-body">
              <h3>${sName}</h3>
              <p>${sDesc}</p>
              ${s.price ? `<span class="service-price">${sanitizeText(s.price)}</span>` : ''}
            </div>
          </div>`;
      }).join('');
      sections.push(`
      <section id="services" class="section section-darker">
        <div class="container">
          <h2 class="section-title">Our Services</h2>
          <div class="services-numbered">${serviceRows}</div>
        </div>
      </section>`);
    }

    // --- Photo Gallery ---
    if (enabledFeatures.has('photo-gallery') && galleryImages.length > 0) {
      const imgs = galleryImages.map(url => `<div class="gallery-item"><img src="${escapeAttr(url)}" alt="${safeName}" loading="lazy"></div>`).join('');
      sections.push(`
      <section id="gallery" class="section section-dark">
        <div class="container">
          <h2 class="section-title">Gallery</h2>
          <div class="gallery-grid">${imgs}</div>
        </div>
      </section>`);
    }

    // --- Testimonials ---
    if (enabledFeatures.has('testimonials') && data.testimonials.length > 0) {
      const cards = data.testimonials.map(t => {
        const tName = sanitizeText(t.name);
        const tContent = sanitizeText(t.content);
        const tRole = t.role ? sanitizeText(t.role) : '';
        return `
          <div class="testimonial-card">
            <div class="testimonial-body">
              <p>&ldquo;${tContent}&rdquo;</p>
            </div>
            <div class="testimonial-author">
              <strong>${tName}</strong>
              ${tRole ? `<span>${tRole}</span>` : ''}
              ${t.rating ? `<div class="testimonial-stars">${renderStars(t.rating)}</div>` : ''}
            </div>
          </div>`;
      }).join('');
      sections.push(`
      <section id="testimonials" class="section section-darker">
        <div class="container">
          <h2 class="section-title">Testimonials</h2>
          <div class="testimonials-list">${cards}</div>
        </div>
      </section>`);
    }

    // --- FAQ ---
    if (enabledFeatures.has('faq') && data.faq && data.faq.length > 0) {
      const items = data.faq.map(f => {
        const q = sanitizeText(f.question);
        const a = sanitizeText(f.answer);
        return `
          <div class="faq-item">
            <h3 class="faq-q">${q}</h3>
            <p class="faq-a">${a}</p>
          </div>`;
      }).join('');
      sections.push(`
      <section id="faq" class="section section-dark">
        <div class="container">
          <h2 class="section-title">FAQ</h2>
          <div class="faq-list">${items}</div>
        </div>
      </section>`);
    }

    // --- Price List ---
    if (enabledFeatures.has('price-list') && data.priceList && data.priceList.length > 0) {
      const rows = data.priceList.map(p => {
        const pName = sanitizeText(p.name);
        const pDesc = sanitizeText(p.description);
        const pPrice = sanitizeText(p.price);
        return `
          <div class="price-row">
            <div class="price-info">
              <h3>${pName}</h3>
              <p>${pDesc}</p>
            </div>
            <span class="price-amount">${pPrice}</span>
          </div>`;
      }).join('');
      sections.push(`
      <section id="pricing" class="section section-darker">
        <div class="container">
          <h2 class="section-title">Pricing</h2>
          <div class="price-list">${rows}</div>
        </div>
      </section>`);
    }

    // --- Business Hours ---
    if (enabledFeatures.has('business-hours') && businessHours.length > 0) {
      const hoursList = businessHours.map(h => `<li>${sanitizeText(h)}</li>`).join('');
      sections.push(`
      <section class="section section-dark">
        <div class="container">
          <h2 class="section-title">Hours</h2>
          <ul class="hours-list">${hoursList}</ul>
        </div>
      </section>`);
    }

    // --- Contact ---
    if (enabledFeatures.has('contact-form')) {
      sections.push(`
      <section id="contact" class="section section-darker">
        <div class="container">
          <h2 class="section-title">Contact Us</h2>
          <div class="contact-cards">
            <div class="contact-card">
              <h3>Call Us</h3>
              ${enabledFeatures.has('phone-display') && phone ? `<div class="contact-item">${getIconSvg('phone')}<a href="tel:${escapeAttr(phone)}">${sanitizeText(phone)}</a></div>` : `<p style="color:var(--text-muted);font-size:.9rem;">Phone coming soon</p>`}
            </div>
            <div class="contact-card">
              <h3>Visit Us</h3>
              ${enabledFeatures.has('address-map') && address ? `<div class="contact-item">${getIconSvg('map-pin')}<span>${sanitizeText(address)}</span></div>` : `<p style="color:var(--text-muted);font-size:.9rem;">Address coming soon</p>`}
              ${enabledFeatures.has('address-map') && googleMapsUrl ? `<a class="map-link" href="${escapeAttr(googleMapsUrl)}" target="_blank" rel="noopener noreferrer">View on Google Maps</a>` : ''}
            </div>
            <div class="contact-card">
              <h3>Write Us</h3>
              <form class="contact-form" ${contactEmail ? `action="https://formsubmit.co/${escapeAttr(contactEmail)}" method="POST"` : 'onsubmit="return false;"'}>
                <input type="text" placeholder="Your Name" required>
                <input type="email" placeholder="Your Email" required>
                <textarea placeholder="Message" rows="4" required></textarea>
                <button type="submit">Send Message</button>
              </form>
            </div>
          </div>
        </div>
      </section>`);
    }

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${safeSeoTitle}</title>
  <meta name="description" content="${safeSeoDesc}">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="${fontsUrl}" rel="stylesheet">
  <style>
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
    :root {
      --primary: #1E3A8A; --bg: #0F1B3D; --bg-alt: #162350; --accent: #D4AF37;
      --text: #E2E8F0; --text-muted: #94A3B8;
      --font-heading: 'EB Garamond', serif; --font-body: 'Open Sans', sans-serif;
    }
    html { scroll-behavior: smooth; }
    body { font-family: var(--font-body); background: var(--bg); color: var(--text); line-height: 1.6; }
    .container { max-width: 1100px; margin: 0 auto; padding: 0 24px; }
    a { color: var(--accent); text-decoration: none; transition: opacity .2s; }
    a:hover { opacity: .8; }

    /* NAV */
    .navbar { position: fixed; top: 0; left: 0; right: 0; z-index: 100; background: rgba(15,27,61,.95); backdrop-filter: blur(8px); border-bottom: 2px solid var(--accent); }
    .nav-inner { display: flex; align-items: center; justify-content: space-between; padding: 16px 24px; max-width: 1100px; margin: 0 auto; }
    .nav-brand { font-family: var(--font-heading); font-size: 1.5rem; color: var(--accent); font-weight: 700; letter-spacing: 1px; }
    .nav-links { display: flex; gap: 28px; list-style: none; }
    .nav-links a { color: var(--text-muted); font-size: .85rem; text-transform: uppercase; letter-spacing: 1.5px; font-weight: 500; }
    .nav-links a:hover { color: var(--accent); }
    .nav-toggle { display: none; background: none; border: none; cursor: pointer; }
    .nav-toggle span { display: block; width: 26px; height: 2px; background: var(--accent); margin: 5px 0; transition: .3s; }

    /* GOLD RULE */
    .gold-rule { width: 80px; height: 3px; background: linear-gradient(90deg, transparent, var(--accent), transparent); margin: 0 auto 24px; }

    /* HERO - sidebar + wide hero area */
    .hero { position: relative; min-height: 100vh; display: flex; overflow: hidden; }
    .hero-sidebar { width: 30%; background: rgba(15,27,61,.98); display: flex; flex-direction: column; justify-content: center; padding: 120px 32px 60px; position: relative; z-index: 2; border-right: 3px solid var(--accent); }
    .hero-sidebar .hero-brand { font-family: var(--font-heading); font-size: 1.6rem; color: var(--accent); margin-bottom: 24px; font-weight: 700; }
    .hero-sidebar .hero-detail { color: var(--text-muted); font-size: .9rem; margin-bottom: 12px; line-height: 1.6; }
    .hero-sidebar .hero-detail .icon-char { color: var(--accent); margin-right: 8px; }
    .hero-main { width: 70%; position: relative; display: flex; align-items: center; }
    .hero-bg { position: absolute; inset: 0; background-size: cover; background-position: center; opacity: .2; }
    .hero-overlay { position: absolute; inset: 0; background: linear-gradient(135deg, rgba(15,27,61,.85), rgba(30,58,138,.7), rgba(15,27,61,.9)); }
    .hero-content { position: relative; z-index: 2; padding: 120px 48px 80px; width: 100%; }
    .hero h1 { font-family: var(--font-heading); font-size: clamp(2.5rem, 5vw, 4rem); line-height: 1.1; font-weight: 700; margin-bottom: 16px; color: var(--accent); }
    .hero-sub { font-size: clamp(1rem, 2vw, 1.15rem); color: var(--text-muted); max-width: 600px; margin-bottom: 32px; }
    .hero-cta { display: inline-block; padding: 16px 44px; background: var(--accent); color: var(--bg); font-family: var(--font-heading); font-size: 1.1rem; letter-spacing: 2px; border: none; cursor: pointer; transition: transform .2s, box-shadow .2s; }
    .hero-cta:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(212,175,55,.3); opacity: 1; color: var(--bg); }

    /* CREST BORDER */
    .crest-border { border: 2px solid var(--accent); border-radius: 4px; position: relative; }
    .crest-border::before { content: ''; position: absolute; inset: 4px; border: 1px solid rgba(212,175,55,.3); border-radius: 2px; pointer-events: none; }

    /* SECTIONS */
    .section { padding: 80px 0; }
    .section-dark { background: var(--bg); }
    .section-darker { background: var(--bg-alt); }
    .section-title { font-family: var(--font-heading); font-size: clamp(2rem, 5vw, 3rem); text-align: center; margin-bottom: 48px; color: var(--accent); }

    /* ABOUT - drop-cap story */
    .about-story { max-width: 800px; margin: 0 auto; font-size: 1.05rem; color: var(--text-muted); line-height: 1.9; }
    .about-story .drop-cap { float: left; font-family: var(--font-heading); font-size: 4.5rem; line-height: .8; padding-right: 12px; padding-top: 6px; color: var(--accent); font-weight: 700; }

    /* SERVICES - numbered list with ornate numbers */
    .services-numbered { display: flex; flex-direction: column; gap: 0; max-width: 800px; margin: 0 auto; }
    .service-row { display: flex; align-items: flex-start; gap: 28px; padding: 32px 0; border-bottom: 1px solid rgba(212,175,55,.2); }
    .service-num { font-family: var(--font-heading); font-size: 3rem; line-height: 1; color: var(--accent); flex-shrink: 0; min-width: 60px; text-align: center; opacity: .7; }
    .service-body { flex: 1; }
    .icon-char { font-size: 28px; line-height: 1; }
    .service-body h3 { font-family: var(--font-heading); font-size: 1.3rem; margin-bottom: 8px; color: var(--accent); }
    .service-body p { color: var(--text-muted); font-size: .95rem; line-height: 1.7; }
    .service-price { display: inline-block; margin-top: 14px; color: var(--accent); font-weight: 700; font-size: 1.1rem; }

    /* GALLERY */
    .gallery-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
    .gallery-item img { width: 100%; height: 240px; object-fit: cover; display: block; border: 2px solid rgba(212,175,55,.3); }

    /* TESTIMONIALS - blockquote with large decorative quotes */
    .testimonials-list { display: flex; flex-direction: column; gap: 40px; max-width: 800px; margin: 0 auto; }
    .testimonial-card { position: relative; padding: 40px 48px 32px; background: var(--bg); border: 1px solid rgba(212,175,55,.2); }
    .testimonial-card::before { content: '\\201C'; font-family: var(--font-heading); font-size: 5rem; color: var(--accent); opacity: .3; position: absolute; top: -10px; left: 20px; line-height: 1; }
    .testimonial-card::after { content: '\\201D'; font-family: var(--font-heading); font-size: 5rem; color: var(--accent); opacity: .3; position: absolute; bottom: -30px; right: 20px; line-height: 1; }
    .testimonial-body p { font-size: 1.1rem; font-style: italic; color: var(--text-muted); line-height: 1.8; margin-bottom: 20px; }
    .testimonial-author strong { display: block; color: var(--text); font-size: 1rem; }
    .testimonial-author span { color: var(--text-muted); font-size: .85rem; }
    .testimonial-stars { margin-top: 8px; }
    .star { color: #475569; font-size: 1rem; }
    .star.filled { color: var(--accent); }

    /* FAQ */
    .faq-list { max-width: 800px; margin: 0 auto; }
    .faq-item { border-bottom: 1px solid rgba(212,175,55,.2); padding: 24px 0; }
    .faq-q { font-family: var(--font-heading); font-size: 1.2rem; margin-bottom: 10px; color: var(--accent); }
    .faq-a { color: var(--text-muted); line-height: 1.7; }

    /* PRICE LIST */
    .price-list { max-width: 800px; margin: 0 auto; }
    .price-row { display: flex; justify-content: space-between; align-items: flex-start; padding: 24px 0; border-bottom: 1px solid rgba(212,175,55,.2); gap: 24px; }
    .price-info h3 { font-family: var(--font-heading); font-size: 1.1rem; margin-bottom: 4px; }
    .price-info p { color: var(--text-muted); font-size: .9rem; }
    .price-amount { font-family: var(--font-heading); font-size: 1.5rem; color: var(--accent); white-space: nowrap; }

    /* HOURS */
    .hours-list { list-style: none; max-width: 400px; margin: 0 auto; }
    .hours-list li { padding: 12px 0; border-bottom: 1px solid rgba(212,175,55,.2); color: var(--text-muted); font-size: .95rem; }

    /* GOOGLE REVIEWS */
    .google-reviews { background: var(--accent); padding: 16px 0; }
    .review-badge { display: flex; align-items: center; justify-content: center; gap: 12px; flex-wrap: wrap; }
    .review-badge .star, .review-badge .star.filled { color: var(--bg); font-size: 1.2rem; }
    .review-text { color: var(--bg); font-weight: 700; font-size: .95rem; }

    /* PROMO */
    .promo-banner { background: rgba(212,175,55,.1); padding: 20px 0; border-top: 1px solid var(--accent); border-bottom: 1px solid var(--accent); }
    .promo-inner { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; justify-content: center; }
    .promo-badge { background: var(--accent); color: var(--bg); font-family: var(--font-heading); padding: 8px 20px; font-size: 1.1rem; }
    .promo-content strong { display: block; margin-bottom: 2px; }
    .promo-content span { color: var(--text-muted); font-size: .9rem; }

    /* CONTACT - three-col cards */
    .contact-cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; align-items: start; }
    .contact-card { background: var(--bg); border: 1px solid rgba(212,175,55,.25); padding: 32px 24px; text-align: center; }
    .contact-card h3 { font-family: var(--font-heading); font-size: 1.1rem; color: var(--accent); margin-bottom: 16px; }
    .contact-item { display: flex; align-items: center; gap: 12px; color: var(--text-muted); justify-content: center; margin-bottom: 8px; }
    .contact-item .icon-char { color: var(--accent); flex-shrink: 0; font-size: 20px; line-height: 1; }
    .contact-item a { color: var(--text); }
    .map-link { display: inline-block; margin-top: 8px; font-weight: 600; text-transform: uppercase; font-size: .85rem; letter-spacing: 1px; }
    .contact-form { display: flex; flex-direction: column; gap: 16px; }
    .contact-form input, .contact-form textarea { background: var(--bg); border: 1px solid rgba(212,175,55,.3); color: var(--text); padding: 14px 16px; font-family: var(--font-body); font-size: .95rem; outline: none; transition: border-color .2s; }
    .contact-form input:focus, .contact-form textarea:focus { border-color: var(--accent); }
    .contact-form button { padding: 16px; background: var(--accent); color: var(--bg); font-family: var(--font-heading); font-size: 1.05rem; letter-spacing: 2px; border: none; cursor: pointer; transition: transform .2s; }
    .contact-form button:hover { transform: translateY(-2px); }

    /* FOOTER */
    .footer { background: var(--bg-alt); border-top: 2px solid var(--accent); padding: 40px 0; text-align: center; }
    .footer p { color: var(--text-muted); font-size: .85rem; }

    /* RESPONSIVE */
    @media (max-width: 768px) {
      .nav-links { display: none; }
      .nav-links.show { display: flex; flex-direction: column; position: absolute; top: 100%; left: 0; right: 0; background: rgba(15,27,61,.98); padding: 16px 24px; gap: 12px; border-bottom: 2px solid var(--accent); }
      .nav-toggle { display: block; }
      .hero { flex-direction: column; }
      .hero-sidebar { width: 100%; padding: 100px 24px 32px; border-right: none; border-bottom: 3px solid var(--accent); }
      .hero-main { width: 100%; }
      .gallery-grid { grid-template-columns: repeat(2, 1fr); }
      .contact-cards { grid-template-columns: 1fr; }
      .price-row { flex-direction: column; gap: 8px; }
    }
    @media (max-width: 480px) {
      .gallery-grid { grid-template-columns: 1fr; }
      .hero-content { padding: 100px 16px 60px; }
    }
  </style>
</head>
<body>
  <nav class="navbar">
    <div class="nav-inner">
      <a href="#" class="nav-brand">${safeName}</a>
      <button class="nav-toggle" aria-label="Toggle menu" onclick="this.parentElement.querySelector('.nav-links').classList.toggle('show')">
        <span></span><span></span><span></span>
      </button>
      <ul class="nav-links">
        ${navLinks.map(l => `<li><a href="${l.href}">${l.label}</a></li>`).join('')}
      </ul>
    </div>
  </nav>

  <section class="hero">
    <div class="hero-sidebar">
      <div class="hero-brand">${safeName}</div>
      ${enabledFeatures.has('phone-display') && phone ? `<div class="hero-detail">${getIconSvg('phone')} ${sanitizeText(phone)}</div>` : ''}
      ${address ? `<div class="hero-detail">${getIconSvg('map-pin')} ${sanitizeText(address)}</div>` : ''}
      ${rating ? `<div class="hero-detail">${getIconSvg('star')} ${rating.toFixed(1)} / 5</div>` : ''}
    </div>
    <div class="hero-main">
      ${heroImage ? `<div class="hero-bg" style="background-image:url('${escapeAttr(heroImage)}')"></div>` : ''}
      <div class="hero-overlay"></div>
      <div class="hero-content">
        <h1>${safeHeadline}</h1>
        <p class="hero-sub">${truncateAtSentence(safeAbout)}</p>
        ${enabledFeatures.has('contact-form') ? `<a href="#contact" class="hero-cta">Contact Us</a>` : ''}
      </div>
    </div>
  </section>

  ${sections.join('\n')}

  <footer class="footer">
    <div class="container">
      <p>${safeName}</p>
    </div>
  </footer>
</body>
</html>`;
  }
};
